/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNetwork } from '../hooks/use-network';
import { useWallet } from '../providers/WalletProvider';
import { ChevronDown, Check } from 'lucide-react';
import { Cluster } from '../types/networks';

export const NetworkSelector: React.FC = () => {
    const { currentCluster, allClusters, switchNetwork } = useNetwork();
    const { switchChain } = useWallet();
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (cluster: Cluster) => {
        // Disconnect wallet if chain changes (e.g., SOL to SUI)
        if (cluster.chain !== currentCluster.chain) {
            switchChain(cluster.chain);
        }
        // Switch network within the same chain (e.g., mainnet to devnet)
        switchNetwork(cluster.network);
        setIsOpen(false);
    };

    const getChainIcon = (chain: 'SOL' | 'SUI') => {
        return chain === 'SUI' 
            ? "https://cryptologos.cc/logos/sui-sui-logo.png" 
            : "https://cryptologos.cc/logos/solana-sol-logo.png";
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1.5 whitespace-nowrap pl-4 pr-3 py-2.5 bg-white hover:bg-stone-50 rounded-md text-xs font-bold text-stone-700 border border-stone-200 shadow-sm transition-colors"
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                <img src={getChainIcon(currentCluster.chain)} className="w-5 h-5" alt={currentCluster.chain} />
                <span className="hidden sm:inline">{currentCluster.name}</span>
                <span className="sm:hidden">{currentCluster.network === 'mainnet' ? 'Mainnet' : 'Devnet'}</span>
                <ChevronDown size={16} className={`text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl border border-stone-200 shadow-lg z-50 p-2 animate-fade-in-up">
                    <div className="text-xs font-bold text-stone-400 uppercase tracking-wider px-2 pt-1 pb-2">Select Network</div>
                    {allClusters.map((cluster) => {
                        const isSelected = cluster.id === currentCluster.id;
                        return (
                            <button
                                key={cluster.id}
                                onClick={() => handleSelect(cluster)}
                                className={`w-full flex items-center justify-between p-2 rounded-md text-sm text-left hover:bg-stone-50 transition-colors ${isSelected ? 'bg-stone-50' : ''}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border ${isSelected ? 'border-nobel-gold bg-stone-100' : 'border-stone-100 bg-white'}`}>
                                        <img src={getChainIcon(cluster.chain)} className="w-5 h-5" alt="" />
                                    </div>
                                    <div>
                                        <div className={`font-bold ${isSelected ? 'text-stone-900' : 'text-stone-600'}`}>
                                            {cluster.chain === 'SOL' ? 'Solana' : 'Sui'}
                                        </div>
                                        <div className="text-xs text-stone-400 font-medium capitalize">
                                            {cluster.network === 'mainnet' ? 'Mainnet Beta' : 'Devnet'}
                                        </div>
                                    </div>
                                </div>
                                {isSelected && <Check size={16} className="text-nobel-gold" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};